import React, { useState } from "react";
import { Card } from "antd";
import { Wind } from "lucide-react";

const beaufortToKnots = {
  0:0, 1:3, 2:6, 3:10, 4:15, 5:21,
  6:27, 7:33, 8:40, 9:47, 10:55, 11:63, 12:75,
};

const beaufortScale = {
  0:  { name: "Calm",          speed: "0 kt"     },
  1:  { name: "Light air",     speed: "1–3 kt"   },
  2:  { name: "Light breeze",  speed: "4–6 kt"   },
  3:  { name: "Gentle breeze", speed: "7–10 kt"  },
  4:  { name: "Mod. breeze",   speed: "11–16 kt" },
  5:  { name: "Fresh breeze",  speed: "17–21 kt" },
  6:  { name: "Strong breeze", speed: "22–27 kt" },
  7:  { name: "Near gale",     speed: "28–33 kt" },
  8:  { name: "Gale",          speed: "34–40 kt" },
  9:  { name: "Strong gale",   speed: "41–47 kt" },
  10: { name: "Storm",         speed: "48–55 kt" },
  11: { name: "Violent storm", speed: "56–63 kt" },
  12: { name: "Hurricane",     speed: "64+ kt"   },
};

const windDirections = {
  N:0, NNE:22.5, NE:45, ENE:67.5,
  E:90, ESE:112.5, SE:135, SSE:157.5,
  S:180, SSW:202.5, SW:225, WSW:247.5,
  W:270, WNW:292.5, NW:315, NNW:337.5,
};

// ── Original wind barb (unchanged) ──────────────────────────────
const getBeaufortWindArrow = (force) => {
  const knots    = beaufortToKnots[force];
  const elements = [];
  if (force === 0) {
    return <g><circle key="calm" cx="0" cy="0" r="6" fill="none" stroke="#dc3545" strokeWidth="2" /></g>;
  }
  elements.push(<line key="shaft" x1="0" y1="0" x2="30" y2="0" stroke="#dc3545" strokeWidth="2" />);
  let rem = knots;
  const flags  = Math.floor(rem / 50); rem -= flags * 50;
  const longs  = Math.floor(rem / 10); rem -= longs * 10;
  const shorts = Math.floor(rem / 5);
  let pos = 30;
  for (let i = 0; i < flags;  i++) { elements.push(<polygon key={`f${i}`} points={`${pos},0 ${pos-10},0 ${pos},-8`} fill="#dc3545" stroke="#dc3545" strokeWidth="1" />); pos -= 10; }
  for (let i = 0; i < longs;  i++) { elements.push(<line key={`l${i}`} x1={pos} y1="0" x2={pos-10} y2="-6" stroke="#dc3545" strokeWidth="2" />); pos -= 4; }
  for (let i = 0; i < shorts; i++) { elements.push(<line key={`s${i}`} x1={pos} y1="0" x2={pos-5}  y2="-3" stroke="#dc3545" strokeWidth="2" />); pos -= 4; }
  return <g>{elements}</g>;
};

const WindDirectionChart = ({ selectedDirection, selectedBeaufort }) => {
  const [currentDirection] = useState(selectedDirection);
  const [currentBeaufort]  = useState(selectedBeaufort ?? 0);

  const getWindPos = (direction) => {
    const angle  = windDirections[direction] || 0;
    const radian = ((angle - 90) * Math.PI) / 180;
    return { x: 150 + 80 * Math.cos(radian), y: 150 + 80 * Math.sin(radian), angle };
  };

  const windPos = getWindPos(currentDirection);

  return (
    <Card
      size="small"
      title={
        <span className="d-flex align-items-center gap-2">
          <Wind size={13} />
          Wind Direction Chart
        </span>
      }
      bodyStyle={{ padding: "6px 8px" }}
    >
      {/* Sub-header */}
      

      <div className="row g-2">

        {/* ── Original compass diagram (unchanged) ── */}
        <div className="col-6 d-flex justify-content-center">
          <svg width="300" height="310">
            {/* Background circle */}
            <circle cx="150" cy="150" r="140"
              fill="rgba(173,216,230,0.3)" stroke="#333" strokeWidth="2" />

            {/* Ship icon — original unchanged */}
            <g transform="translate(150,150) scale(1)">
              <svg x="-40" y="-70" width="80" height="140" viewBox="0 0 64 128">
                <path d="M32 4 C28 18, 22 38, 22 56 L22 110 C22 118, 27 124, 32 126 C37 124, 42 118, 42 110 L42 56 C42 38, 36 18, 32 4 Z"
                  fill="#ecf0f1" stroke="#2c3e50" strokeWidth="2" />
                <rect x="26" y="12" width="12" height="10" rx="2" fill="#bdc3c7" stroke="#2c3e50" strokeWidth="1" />
                <rect x="28" y="8"  width="8"  height="6"  rx="1" fill="#95a5a6" stroke="#2c3e50" strokeWidth="0.5" />
                {Array.from({ length: 7 }).map((_, i) => (
                  <rect key={i} x="26" y={26 + i * 12} width="12" height="8"
                    fill="#dfe6e9" stroke="#2c3e50" strokeWidth="0.8" />
                ))}
                <path d="M32 4 L30 10 L34 10 Z" fill="#2c3e50" />
                <path d="M22 110 Q32 124 42 110" fill="none" stroke="#2c3e50" strokeWidth="1.5" />
              </svg>
            </g>

            {/* Compass labels */}
            {Object.entries(windDirections).map(([dir, angle]) => {
              const radian = ((angle - 90) * Math.PI) / 180;
              const x = 150 + 130 * Math.cos(radian);
              const y = 150 + 130 * Math.sin(radian);
              return (
                <text key={dir} x={x} y={y}
                  textAnchor="middle" dominantBaseline="middle"
                  fontSize="12"
                  fill={dir === currentDirection ? "#dc3545" : "#666"}
                  fontWeight={dir === currentDirection ? "bold" : "normal"}
                >
                  {dir}
                </text>
              );
            })}

            {/* Wind indicator */}
            <g transform={`translate(${windPos.x},${windPos.y})`}>
              <g transform={`rotate(${windPos.angle + 180})`}>
                {getBeaufortWindArrow(currentBeaufort)}
              </g>
            </g>
          </svg>
        </div>

        {/* ── Beaufort 3-column grid ── */}
        <div className="col-6">
          <div className="d-flex justify-content-between">

      
          <div className="fw-semibold mb-1" style={{ fontSize: 12, color: "#444" }}>
            Beaufort Scale Reference
          </div>
          <div className="text-muted mb-2" style={{ fontSize: 12 }}>
        Direction: <strong className="text-dark">{currentDirection || "—"}</strong>
        &nbsp;|&nbsp;Force: <strong className="text-dark">{currentBeaufort}</strong>
        &nbsp;({beaufortScale[currentBeaufort]?.name})
            </div>
      </div>
          <div className="row g-1">
            {Object.entries(beaufortScale).map(([force, data]) => {
              const isActive = Number(force) === currentBeaufort;
              return (
                <div key={force} className="col-4">
                  <div
                    className={`rounded text-center p-1 h-100 ${isActive
                      ? "border border-success border-opacity-50"
                      : "border"}`}
                    style={{
                      background: isActive ? "#e6f4f1" : "#fafafa",
                      fontSize: 12,
                    }}
                  >
                    {/* Mini barb */}
                    <svg width="32" height="14" viewBox="-4 -8 40 16" className="m-auto" style={{ marginBottom: 2 }}>
                      {getBeaufortWindArrow(Number(force))}
                    </svg>
                    <div className="fw-bold" style={{ fontSize: 12, color: isActive ? "#0C9377" : "#222" }}>
                      Force {force}
                    </div>
                    <div style={{ fontSize: 12, color: "#555" }}>{data.name}</div>
                    <div style={{ fontSize: 12, color: "#999" }}>{data.speed}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </Card>
  );
};

export default WindDirectionChart;